import { useState, useEffect } from 'react';
import { thumbUrl } from '../utils/listingImages';

/**
 * Drop-in <img> for LISTING PHOTO THUMBNAIL slots (TECH_DEBT.md R5).
 *
 * Renders the ~640px `_thumb640` derivative of `src` and degrades through a
 * one-way chain: thumb → original → fallbackSrc (the caller's placeholder,
 * e.g. /images/top-diagram.png). A missing derivative therefore costs one
 * extra request and renders exactly what the site rendered before R5 — which
 * is what makes partial thumb coverage safe in any rollout order.
 *
 * NOT for full-size surfaces (PedalDetails/ArchivedListing heroes,
 * ImageOverlay, purchase hero) — those must keep original bytes so buyers can
 * inspect condition.
 *
 * All other props (className, style, loading, fetchpriority, alt, handlers)
 * pass straight through to the <img>.
 */
const STAGE_THUMB = 0;
const STAGE_ORIGINAL = 1;
const STAGE_FALLBACK = 2;

export default function ListingThumb({ src, fallbackSrc, onError, ...imgProps }) {
  const [stage, setStage] = useState(STAGE_THUMB);

  // New src (carousel next/prev, list re-sort) restarts the chain.
  useEffect(() => {
    setStage(STAGE_THUMB);
  }, [src]);

  const derived = thumbUrl(src);
  // When derivation is a no-op (non-Storage URL) the thumb stage IS the
  // original — skip straight past it so a failure goes directly to fallback.
  const effectiveStage = stage === STAGE_THUMB && derived === src ? STAGE_ORIGINAL : stage;

  let renderSrc;
  if (effectiveStage === STAGE_THUMB) renderSrc = derived;
  else if (effectiveStage === STAGE_ORIGINAL) renderSrc = src;
  else renderSrc = fallbackSrc || src;

  const handleError = (e) => {
    if (effectiveStage === STAGE_THUMB) {
      setStage(STAGE_ORIGINAL);
    } else if (effectiveStage === STAGE_ORIGINAL && fallbackSrc) {
      setStage(STAGE_FALLBACK);
    } else if (onError) {
      // End of our chain — let the caller's handler run (some sites swap in
      // their own placeholder via e.currentTarget.src).
      onError(e);
    }
    // At STAGE_FALLBACK with no caller handler: do nothing — no loop.
  };

  return <img src={renderSrc} onError={handleError} {...imgProps} />;
}
